import { BUMP_MEDIA_GENERATION, MEDIA_GENERATION_BUMPED } from '@/lib/media/mediaCacheGeneration'
import {
  clearCachedAttachments,
  invalidateAttachmentWrites,
} from '@/lib/prefetch/offlineAttachmentCache'
import { abortActivePrefetches } from '@/lib/prefetch/activePrefetches'
import { invalidateProbeCache, probeConnectivity } from '@/lib/connectivityProbe'

/**
 * Account boundary for cached media (AUTHZ-02 E2).
 *
 * INVARIANT: an entry cached under an unsigned key is a local credential, so it must not survive
 * a change of user. The service worker answers a media request from `image-cache`
 * (StaleWhileRevalidate) or `media-uploads-cache` (CacheFirst) before the server can check who
 * is asking, and native reads `urlMap` without asking at all; and the key is the URL without
 * its signature (`mediaCacheKey`), so once media is signed, holding the entry is as good as
 * holding a valid signature that never expires.
 *
 * One clearing routine (`purgeAccountMedia` on logout, `ensureMediaPrincipal` on a user change),
 * in this order:
 * 1. `invalidateAttachmentWrites` — every page-side write (native file / Dexie / urlMap, the web
 *    prefetch's Cache API put) that has not committed yet is refused from here on;
 * 2. abort the stage downloads and wait for them to settle (`abortActivePrefetches`);
 * 3. `BUMP_MEDIA_GENERATION` to the service worker and wait for its reply, so no SW write that
 *    started for the previous user can commit after step 4 (`mediaCacheGeneration.ts`);
 * 4. delete both caches together with `clearCachedAttachments()`, all awaited.
 * Every wait is bounded (`PREFETCH_SETTLE_TIMEOUT_MS`, `SW_GENERATION_ACK_TIMEOUT_MS`): with no
 * service worker, or one that never replies (a worker from before this build ignores the message),
 * the purge still deletes, just without the SW-side guarantee. It never rejects.
 *
 * It runs on logout (`useUserStore.logoutUser`, which its caller awaits) and when a signed-in
 * user is set while the device's `lastMediaPrincipal` marker names somebody else OR is missing
 * — a missing marker (the first run of this build) does not prove whose entries are cached. The
 * marker is written only after that purge has finished. Logout leaves the marker in place, so a
 * write that slipped past a logout purge is purged again when a different user signs in.
 *
 * A marker naming somebody else purges at once, offline too. A MISSING marker is no evidence of
 * another user either, and purging on it offline would delete the stages a field user downloaded
 * before updating the app, on the first cold start on site. So that purge waits for confirmed
 * connectivity (`probeConnectivity`, not `navigator.onLine`): until then the user is adopted
 * provisionally — in module memory, no marker written — and the purge is owed. It runs at the
 * first confirmed probe: the next `ensureMediaPrincipal`, a window `online` event, or a stage
 * download, which waits for it and does not start while it is still owed
 * (`mediaPurgeBeforeDownload`). A different user while one is adopted provisionally purges at
 * once, as for a marker.
 *
 * Step 2 aborts only the downloads registered when the purge looks for them, so a stage download
 * registered later writes past that purge's deletes. A download therefore registers only while no
 * purge is under way, queued or owed, and asks again after every wait (`mediaPurgeBeforeDownload`)
 * — a different user can be queued behind the purge it waited for. One that waited across a
 * logout does not start at all.
 *
 * Accepted by that deferral:
 * - on a shared web browser, SW media entries from before this build stay until the first start
 *   with network;
 * - "confirmed" means a response from the server's host, not that the API is healthy. On native
 *   that is the API host (`connectivityProbe.ts`): the app's own `/version.json` is served
 *   from the bundle there and would confirm nothing.
 *
 * Not covered, by design: the anonymous public preview has no logout, so its entries live until
 * they expire, as before.
 */

export const MEDIA_CACHE_NAMES = ['image-cache', 'media-uploads-cache'] as const
export const LAST_MEDIA_PRINCIPAL_KEY = 'lastMediaPrincipal'
export const PREFETCH_SETTLE_TIMEOUT_MS = 5000
export const SW_GENERATION_ACK_TIMEOUT_MS = 2000

/**
 * - `acked`: the worker confirmed the new generation;
 * - `no-worker`: no service worker controls or is active for this page;
 * - `timeout`: no reply within the bound (e.g. a worker from before this build);
 * - `failed`: the worker replied with an error, or the message could not be posted.
 */
export type ServiceWorkerBump = 'acked' | 'no-worker' | 'timeout' | 'failed'

export type MediaPurgeOutcome = {
  prefetchesSettled: boolean
  serviceWorker: ServiceWorkerBump
}

type PurgeInFlight = { done: Promise<MediaPurgeOutcome>; endsSession: boolean }

// Every purge still running, whoever started it: `pendingPrincipal` sees only the ones
// `ensureMediaPrincipal` starts, and logout calls `purgeAccountMedia` directly.
const purgesInFlight = new Set<PurgeInFlight>()
// Logout purges started so far, so a stage download that waited across one learns that the
// session it was requested in has ended, even when that purge has already finished.
let sessionsEnded = 0

/**
 * The logout purge: clears the media caches and ends the session, so a stage download still
 * waiting at the boundary does not start (`mediaPurgeBeforeDownload`). `ensureMediaPrincipal`
 * runs the same routine without ending the session.
 */
export function purgeAccountMedia(): Promise<MediaPurgeOutcome> {
  return runPurge(true)
}

function runPurge(endsSession: boolean): Promise<MediaPurgeOutcome> {
  if (endsSession) sessionsEnded += 1
  const purge: PurgeInFlight = { done: clearAccountMedia(), endsSession }
  purgesInFlight.add(purge)
  const settle = () => void purgesInFlight.delete(purge)
  void purge.done.then(settle, settle)
  return purge.done
}

async function clearAccountMedia(): Promise<MediaPurgeOutcome> {
  invalidateAttachmentWrites()
  const prefetchesSettled = await abortActivePrefetches(PREFETCH_SETTLE_TIMEOUT_MS)
  const serviceWorker = await bumpServiceWorkerMediaGeneration(SW_GENERATION_ACK_TIMEOUT_MS)
  await Promise.all([
    ...MEDIA_CACHE_NAMES.map(deleteCache),
    clearCachedAttachments().catch(() => undefined),
  ])
  return { prefetchesSettled, serviceWorker }
}

async function deleteCache(name: string): Promise<void> {
  if (typeof caches === 'undefined') return
  try {
    await caches.delete(name)
  } catch {
    // Best-effort: the marker check at the next sign-in purges again.
  }
}

/**
 * Asks the service worker to move to the next media cache generation and waits for its reply,
 * for at most `timeoutMs`. Prefers the controller; without one, the active worker of this
 * page's registration, which may still be writing for another tab. Never rejects.
 */
export async function bumpServiceWorkerMediaGeneration(
  timeoutMs: number,
): Promise<ServiceWorkerBump> {
  let timer: ReturnType<typeof setTimeout> | undefined
  let channel: MessageChannel | undefined
  const timedOut = new Promise<ServiceWorkerBump>(resolve => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs)
  })

  const asked = (async (): Promise<ServiceWorkerBump> => {
    const container = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker
    if (!container) return 'no-worker'
    const worker =
      container.controller ??
      (await container.getRegistration().catch(() => undefined))?.active ??
      null
    if (!worker) return 'no-worker'

    const ports = new MessageChannel()
    channel = ports
    return new Promise<ServiceWorkerBump>(resolve => {
      ports.port1.onmessage = event => {
        const reply = event.data as { type?: unknown; ok?: unknown } | null
        if (reply?.type !== MEDIA_GENERATION_BUMPED) return
        resolve(reply.ok === true ? 'acked' : 'failed')
      }
      try {
        worker.postMessage({ type: BUMP_MEDIA_GENERATION }, [ports.port2])
      } catch {
        resolve('failed')
      }
    })
  })().catch((): ServiceWorkerBump => 'failed')

  try {
    return await Promise.race([asked, timedOut])
  } finally {
    clearTimeout(timer)
    channel?.port1.close()
  }
}

// When localStorage cannot be read or written, the marker lives here for the rest of the
// session — otherwise every `setUser` would purge the caches again.
let principalInMemory: string | null = null
let pendingPrincipal: { principal: string; done: Promise<void> } | null = null
// Adopted while the marker was missing and connectivity was not confirmed: the purge the missing
// marker calls for is owed, and no marker is written until it has run. Memory only, so a restart
// asks again.
let provisionalPrincipal: string | null = null
let onlineListenerArmed = false

function deferPurge(principal: string): void {
  provisionalPrincipal = principal
  if (onlineListenerArmed || typeof window === 'undefined') return
  window.addEventListener('online', retryDeferredPurge)
  onlineListenerArmed = true
}

function endDeferral(): void {
  provisionalPrincipal = null
  if (!onlineListenerArmed) return
  window.removeEventListener('online', retryDeferredPurge)
  onlineListenerArmed = false
}

// Also after a logout: the purge then finds empty caches and the marker names the user who left,
// which a different user's sign-in purges on.
function retryDeferredPurge(): void {
  if (provisionalPrincipal === null) return
  // The cached probe answer may predate the event.
  invalidateProbeCache()
  void ensureMediaPrincipal(provisionalPrincipal)
}

function readPrincipal(): string | null {
  try {
    return localStorage.getItem(LAST_MEDIA_PRINCIPAL_KEY) ?? principalInMemory
  } catch {
    return principalInMemory
  }
}

function writePrincipal(principal: string): void {
  principalInMemory = principal
  try {
    localStorage.setItem(LAST_MEDIA_PRINCIPAL_KEY, principal)
  } catch {
    // Kept in memory above.
  }
}

/**
 * Makes sure the media caches on this device belong to `userId` before it is recorded as their
 * owner: purges when the marker names someone else, then writes the marker. When the marker is
 * missing it purges only with connectivity confirmed; otherwise it adopts `userId` provisionally
 * and leaves the purge owed (see the header). Concurrent calls for the same user share one purge;
 * a call for another user queues behind it. Never rejects.
 */
export function ensureMediaPrincipal(userId: number | string): Promise<void> {
  const principal = String(userId)
  if (pendingPrincipal?.principal === principal) return pendingPrincipal.done
  if (!pendingPrincipal && readPrincipal() === principal) {
    endDeferral()
    return Promise.resolve()
  }

  const before = pendingPrincipal?.done ?? Promise.resolve()
  const done = before
    .then(async () => {
      const owner = readPrincipal()
      if (owner === principal) return endDeferral()
      const nobodyElse =
        owner === null && (provisionalPrincipal === null || provisionalPrincipal === principal)
      if (nobodyElse && !(await probeConnectivity())) return deferPurge(principal)
      await runPurge(false)
      writePrincipal(principal)
      endDeferral()
    })
    // The purge does not reject; if it ever did, no marker is written and the next
    // `setUser` tries again.
    .catch(() => undefined)
  const entry = { principal, done }
  pendingPrincipal = entry
  void done.then(() => {
    if (pendingPrincipal === entry) pendingPrincipal = null
  })
  return done
}

/**
 * For a stage download, called BEFORE it registers in `activePrefetches` — registered, it would
 * be aborted by the very purge it waits for. `null` when no purge is under way, queued or owed:
 * register at once, in the same synchronous step. Otherwise a promise that waits for every purge
 * under way (logout included) and runs an owed one if connectivity is confirmed now.
 *
 * It resolves `false` when the download must not start: the purge is still owed (it would later
 * delete what the download writes), or a logout purge was under way or started while it waited
 * (the session the download was requested in is gone). `true` does NOT mean "start": another
 * purge may have begun while these finished — a different user queued behind them — and a run
 * registered after a purge has looked for downloads to abort writes past that purge's deletes. So
 * ask again, and start only on `null`. Never rejects.
 */
export function mediaPurgeBeforeDownload(): Promise<boolean> | null {
  if (!pendingPrincipal && provisionalPrincipal === null && purgesInFlight.size === 0) return null
  const inFlight = [...purgesInFlight]
  const sessionEnding = inFlight.some(purge => purge.endsSession)
  const sessionsEndedBefore = sessionsEnded
  const sessionEnded = () => sessionEnding || sessionsEnded !== sessionsEndedBefore
  const underWay = [pendingPrincipal?.done, ...inFlight.map(purge => purge.done)]
  return (async () => {
    await Promise.all(underWay.map(done => done?.catch(() => undefined)))
    if (sessionEnded()) return false
    const owed = provisionalPrincipal
    if (owed === null) return true
    // A user is waiting on this answer; do not reuse one cached from before they tapped.
    invalidateProbeCache()
    await ensureMediaPrincipal(owed)
    return provisionalPrincipal === null && !sessionEnded()
  })()
}
